import { BusinessScenario } from '@/types/scenarios/business';
import { ShoppingBag, Users, TrendingUp, Clock, DollarSign, Star } from 'lucide-react';

export const smallRetail: BusinessScenario = {
  meta: {
    id: 'small-retail',
    slug: 'small-retail',
    name: 'Small Retail Business',
    tagline: 'Transform your retail customer experience with AI-powered assistance',
    description: 'Perfect for retail stores with 10-50 employees looking to compete with larger chains through superior customer service and personalized shopping experiences.',
    size: 'small',
    employeeCount: '10-50',
    annualRevenue: '$500K - $5M',
    challenges: [
      'Limited staff for customer service during peak hours',
      'Competing with big box stores and online retailers',
      'Managing product inquiries and inventory questions',
      'Building customer loyalty in a competitive market',
      'Handling returns and exchanges efficiently'
    ],
    goals: [
      'Provide 24/7 customer support without additional staff',
      'Increase average order value through personalized recommendations',
      'Reduce cart abandonment rates',
      'Build stronger customer relationships',
      'Streamline the purchasing process'
    ],
    characteristics: [
      'Local or regional presence',
      'Focus on customer relationships',
      'Need for cost-effective solutions',
      'Limited IT resources'
    ],
    isFeatured: true
  },
  
  hero: {
    headline: 'Empower Your Small Retail Business',
    subheadline: 'Deliver big-brand customer experiences with AI-powered voice assistants',
  },
  
  recommendedPlaybooks: [
    'retail-product-assistant',
    'personal-shopper',
    'interactive-faq',
    'limited-time-offer',
    'exit-intent-offer',
    'social-proof-messenger'
  ],
  
  useCases: {
    primary: [
      {
        id: 'product-inquiries',
        title: 'Product Information & Availability',
        description: 'Answer customer questions about products, inventory, and specifications instantly',
        icon: ShoppingBag,
        playbookIds: ['retail-product-assistant', 'interactive-faq'],
        priority: 'high',
        expectedOutcome: 'Reduce product inquiry calls by 70%'
      },
      {
        id: 'personalized-shopping',
        title: 'Personalized Shopping Assistant',
        description: 'Guide customers to perfect products based on their needs and preferences',
        icon: Star,
        playbookIds: ['personal-shopper'],
        priority: 'high',
        expectedOutcome: 'Increase average order value by 25%'
      },
      {
        id: 'customer-support',
        title: '24/7 Customer Support',
        description: 'Handle returns, exchanges, and general inquiries round the clock',
        icon: Users,
        playbookIds: ['interactive-faq', 'smart-escalation'],
        priority: 'high',
        expectedOutcome: 'Improve customer satisfaction scores by 40%'
      }
    ],
    secondary: [
      {
        id: 'promotions',
        title: 'Promotional Campaigns',
        description: 'Engage customers with timely offers and discounts',
        icon: TrendingUp,
        playbookIds: ['limited-time-offer', 'exit-intent-offer'],
        priority: 'medium',
        expectedOutcome: 'Boost conversion rates by 15%'
      }
    ]
  },
  
  benefits: [
    {
      title: 'Compete with Big Retailers',
      description: 'Offer superior, personalized service that large chains cannot match',
      metric: '3x better customer experience scores',
      icon: TrendingUp
    },
    {
      title: 'Reduce Operating Costs',
      description: 'Handle more customers without hiring additional staff',
      metric: 'Save $3,000+ per month',
      icon: DollarSign
    },
    {
      title: 'Always Available',
      description: 'Never miss a sale with 24/7 customer assistance',
      metric: '24/7 availability',
      icon: Clock
    },
    {
      title: 'Build Customer Loyalty',
      description: 'Create memorable shopping experiences that bring customers back',
      metric: '45% increase in repeat customers',
      icon: Star
    }
  ],
  
  metrics: {
    averageROI: '320%',
    implementationTime: '2-3 days',
    supportTicketReduction: '65%',
    customerSatisfaction: '4.7/5',
    costSavings: '$3,500/month'
  },
  
  integrations: {
    essential: [
      'Point of Sale (POS) systems',
      'Inventory management',
      'E-commerce platform'
    ],
    recommended: [
      'Email marketing tools',
      'Customer loyalty programs',
      'Social media platforms'
    ]
  },
  
  demoConfig: {
    firstMessage: "Welcome to our store! I'm here to help you find exactly what you're looking for. Whether you need product information, want to check availability, or have questions about our services, just ask!",
    systemPrompt: "You are a helpful retail assistant for a small retail business. Be friendly, knowledgeable about products, and focused on providing personalized recommendations. Always aim to enhance the shopping experience.",
    exampleQuestions: [
      "Do you have this item in stock?",
      "What are your store hours?",
      "Can you help me find a gift for my wife?",
      "What's your return policy?"
    ]
  },
  
  successStories: [
    {
      companyName: 'Boutique Boulevard',
      companySize: '15 employees',
      industry: 'Fashion Retail',
      challenge: 'Struggling to compete with online retailers and provide adequate customer service during peak shopping hours',
      solution: 'Implemented Help Genie voice assistants for product inquiries and personalized shopping assistance',
      results: [
        'Increased sales by 35% in first quarter',
        'Reduced customer service calls by 70%',
        'Improved customer satisfaction from 3.2 to 4.8 stars',
        'Saved $4,000/month on staffing costs'
      ],
      quote: {
        text: "Help Genie transformed our small boutique into a tech-savvy shopping destination. Our customers love the instant, personalized assistance, and we've seen remarkable growth without adding staff.",
        author: 'Sarah Martinez',
        title: 'Owner'
      }
    }
  ],
  
  resources: [
    {
      type: 'guide',
      title: 'Small Retail Success with AI',
      description: 'Complete guide to implementing voice assistants in retail',
      url: '/resources/small-retail-guide'
    },
    {
      type: 'template',
      title: 'Retail Conversation Scripts',
      description: 'Pre-built conversation flows for common retail scenarios',
      url: '/templates/retail-conversations',
      downloadable: true
    },
    {
      type: 'video',
      title: 'Retail Demo: See It In Action',
      description: 'Watch how small retailers use Help Genie',
      url: '/videos/retail-demo',
      duration: '8 minutes'
    }
  ],
  
  faqs: [
    {
      question: 'How quickly can I get started?',
      answer: 'Most small retail businesses are up and running within 2-3 days. Our platform is designed for quick setup with pre-built templates specifically for retail.'
    },
    {
      question: 'Do I need technical expertise?',
      answer: 'No technical expertise required! Our platform is designed for business owners, not IT professionals. We provide full support and training.'
    },
    {
      question: 'Can it integrate with my existing POS system?',
      answer: 'Yes, Help Genie integrates with most popular POS systems and e-commerce platforms. We can help you connect your existing tools.'
    },
    {
      question: "What if my customers prefer human interaction?",
      answer: "Help Genie enhances, not replaces, human interaction. It handles routine queries instantly, freeing your staff to provide high-value personal service where it matters most."
    }
  ]
};